import { isSameSeamLine, pricesAgree, wordsOf } from './receiptTiling';
import type { DetectedFoodItem } from '../types/foodRecognition';

/**
 * What the cook's taps on "Confirmer les articles" say about the scan.
 *
 * The screen already takes corrections: a cook unchecks a row the scan got
 * wrong. Until #750 nothing kept them, so the same mistake could be made on
 * every receipt and the only signal was someone shaking the phone. This turns
 * the rows as confirmed into a labelled record: which rows were right, which
 * the cook removed, and why the removal most likely happened.
 *
 * The "why" is an inference and is named as one. An unchecked row that looks
 * like a row the cook kept is a duplicate the stitch missed; an unchecked row
 * that looks like nothing else is a product the cook does not track.
 */

export type ScanRowVerdict =
  /** Offered checked, confirmed checked. */
  | 'kept'
  /** Offered checked, unchecked by the cook, and it resembles a kept row. */
  | 'missed_duplicate'
  /** Offered checked, unchecked by the cook, resembles nothing kept. */
  | 'unwanted'
  /** Offered unchecked by the app as a duplicate, left that way. */
  | 'flagged_duplicate'
  /** Offered unchecked by the app as a duplicate, checked back by the cook. */
  | 'restored';

/** A row of the review screen, as it stands when the cook confirms. */
export interface ScanReviewInputRow
  extends Pick<DetectedFoodItem, 'name' | 'receipt_line' | 'total_price' | 'unit_price' | 'quantity' | 'unit' | 'category'> {
  isAccepted: boolean;
  /** Set by `flagCaptureDuplicates`: the app offered this row unchecked. */
  isDuplicate?: boolean;
  duplicateReason?: 'inventory' | 'batch';
}

export interface ScanReviewRow {
  name: string;
  receiptLine?: string;
  price?: number;
  quantity?: number;
  unit?: string;
  category?: string;
  verdict: ScanRowVerdict;
  /** For `missed_duplicate`: the kept row it was taken for. */
  duplicateOf?: string;
  /** For the two verdicts the app's own flag led to. */
  flaggedAs?: 'inventory' | 'batch';
}

export interface ScanReviewSummary {
  rows: number;
  kept: number;
  missed_duplicates: number;
  unwanted: number;
  flagged_duplicates: number;
  restored: number;
}

export interface ScanReview {
  rows: ScanReviewRow[];
  summary: ScanReviewSummary;
}

/**
 * Whether a removed row and a kept row are most likely one product.
 *
 * Wider than the stitch's own test, on purpose. The stitch must never join two
 * real products, so it asks for the same printed text or a cut word. Here the
 * cook has already said the row is wrong; the only question is which kind of
 * wrong, so a shared word at the same price is enough ("Pommes SH" and "Pommes
 * Granny Smith", both 4,29).
 */
export const looksLikeSameProduct = (
  removed: ScanReviewInputRow,
  kept: ScanReviewInputRow,
): boolean => {
  if (isSameSeamLine(removed, kept)) return true;
  const samePrice =
    typeof removed.total_price === 'number' &&
    typeof kept.total_price === 'number' &&
    pricesAgree(removed, kept);
  if (!samePrice) return false;
  const keptWords = new Set(wordsOf(kept.name));
  return wordsOf(removed.name).some((w) => w.length > 2 && keptWords.has(w));
};

export const buildScanReview = (input: ScanReviewInputRow[]): ScanReview => {
  const kept = input.filter((row) => row.isAccepted);

  const rows = input.map((row): ScanReviewRow => {
    const base = {
      name: row.name,
      ...(row.receipt_line ? { receiptLine: row.receipt_line } : {}),
      ...(typeof row.total_price === 'number' ? { price: row.total_price } : {}),
      ...(typeof row.quantity === 'number' ? { quantity: row.quantity } : {}),
      ...(row.unit ? { unit: row.unit } : {}),
      ...(row.category ? { category: row.category } : {}),
    };

    if (row.isDuplicate) {
      return {
        ...base,
        verdict: row.isAccepted ? 'restored' : 'flagged_duplicate',
        ...(row.duplicateReason ? { flaggedAs: row.duplicateReason } : {}),
      };
    }
    if (row.isAccepted) return { ...base, verdict: 'kept' };

    const twin = kept.find((candidate) => looksLikeSameProduct(row, candidate));
    return twin
      ? { ...base, verdict: 'missed_duplicate', duplicateOf: twin.name }
      : { ...base, verdict: 'unwanted' };
  });

  const count = (verdict: ScanRowVerdict) => rows.filter((r) => r.verdict === verdict).length;
  return {
    rows,
    summary: {
      rows: rows.length,
      kept: count('kept'),
      missed_duplicates: count('missed_duplicate'),
      unwanted: count('unwanted'),
      flagged_duplicates: count('flagged_duplicate'),
      restored: count('restored'),
    },
  };
};
